import { useState } from 'react';
import Highlighter from 'react-highlight-words';

import { find, isEmpty, map, some } from 'lodash';

import RenderIf from '@common/RenderIf';
import { faCheck, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CollapsibleArea } from './blocks/collapsibleArea';
import { SearchTab } from './blocks/searchTab';
import { SearchTab as SearchTabType, SelectedTabItem, toSelectedTabItem } from './common/types';

import './searchTabs.scss';

interface Props {
    tabs: SearchTabType[];
    style?: React.CSSProperties;
    onTabItemSelect?: (item: SelectedTabItem, key: string) => void;
    selectedTabItems?: {
        [key: string]: SelectedTabItem[];
    };
    searchValue?: string;
    showTabData?: boolean;
    onTabItemQuantityChange?: (item: SelectedTabItem, key: string, value: string) => void;
    /**
     * Открытая вкладка снаружи. Нужна, чтобы кнопка «Добавить» в таблице заказа
     * сразу раскрывала свой раздел: услуги — из таблицы услуг и так далее.
     * Без неё вкладку выбирает только сам компонент.
     */
    activeTabKey?: string;
    onActiveTabChange?: (key: string) => void;
    /**
     * Результаты поиска свёрнуты. Кнопка сворачивания появляется, только если
     * владелец передал `onCollapsedChange` и есть что сворачивать.
     */
    collapsed?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
}

export const SearchTabs: React.FC<Props> = ({
    tabs,
    style,
    onTabItemSelect = (item: SelectedTabItem, key: string) => {
        console.log('🚀 ~ SearchTabs ~ item:', item);
        console.log('🚀 ~ SearchTabs ~ key:', key);
    },
    selectedTabItems = {},
    searchValue,
    showTabData,
    onTabItemQuantityChange,
    activeTabKey,
    onActiveTabChange,
    collapsed = false,
    onCollapsedChange,
}) => {
    const [ownActiveTab, setOwnActiveTab] = useState<string>(null);

    // Вкладку ведёт владелец, если передал её; иначе компонент сам, как прежде
    const isControlled = activeTabKey !== undefined;
    const activeTab = isControlled ? activeTabKey : ownActiveTab;

    const handleSelectTab = (key: string) => {
        const nextTab = activeTab === key ? null : key;

        if (isControlled) {
            onActiveTabChange?.(nextTab);
            return;
        }
        setOwnActiveTab(nextTab);
    };

    const hasResults =
        Boolean(searchValue || showTabData) && some(tabs, (tab) => !isEmpty(tab.data));
    const canCollapse = Boolean(onCollapsedChange) && hasResults;

    return (
        <div style={{ ...style, position: 'relative' }} className="hide-scrollbar">
            <div
                className="search-tabs"
                style={{
                    borderBottom: activeTab ? 'none' : '',
                    minHeight: activeTab ? 'auto' : '',
                }}
            >
                {canCollapse && (
                    <button
                        type="button"
                        className="search-tabs-collapse"
                        onClick={() => onCollapsedChange(!collapsed)}
                        aria-label={collapsed ? 'Развернуть результаты' : 'Свернуть результаты'}
                    >
                        <FontAwesomeIcon
                            icon={faChevronUp}
                            size='2x'
                            className={collapsed ? 'search-tabs-collapse__icon--down' : undefined}
                        />
                    </button>
                )}
                {map(tabs, (tab) => {
                    return (
                        <SearchTab
                            key={tab.key}
                            tab={tab}
                            activeTab={activeTab}
                            onChange={handleSelectTab}
                            selectedItems={selectedTabItems[tab.key]}
                            onSelectItem={onTabItemSelect}
                            showTabData={showTabData}
                            onTabItemQuantityChange={onTabItemQuantityChange}
                            collapsed={collapsed}
                        />
                    );
                })}
            </div>
            {!!activeTab && (
                <CollapsibleArea isOpen={!collapsed}>
                    <div className="search-tabs-content">
                        {map(
                            find(tabs, (tab) => tab.key === activeTab)?.data,
                            ({ data: item, type }) => {
                                return (
                                    <div
                                        key={item.id}
                                        className="search-tabs-content-item"
                                        onClick={() =>
                                            onTabItemSelect(
                                                toSelectedTabItem(item, type),
                                                activeTab
                                            )
                                        }
                                        style={{
                                            background: some(selectedTabItems[activeTab], {
                                                id: item.id,
                                            })
                                                ? '#D0E0C5'
                                                : 'white',
                                        }}
                                    >
                                        <RenderIf condition={typeof searchValue == 'string'}>
                                            <div>
                                                <Highlighter
                                                    searchWords={searchValue?.split(' ') || []}
                                                    autoEscape={true}
                                                    textToHighlight={item.name}
                                                />
                                            </div>
                                        </RenderIf>
                                        <RenderIf condition={typeof searchValue !== 'string'}>
                                            <div>{item.name}</div>
                                        </RenderIf>
                                        {some(selectedTabItems[activeTab], { id: item.id }) && (
                                            <div style={{ opacity: '.3' }}>
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    color="green"
                                                    size="xl"
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                        )}
                    </div>
                </CollapsibleArea>
            )}
        </div>
    );
};
